"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "utils";
import type {
  HeatmapLayoutNode,
  HeatmapProps,
  HeatmapRenderContext,
} from "./types";
import { computeHeatmapLayout } from "./heatmapLayout";
import {
  getFillColor,
  getTextColor,
  getBorderStyle,
  DEFAULT_BORDER_RADIUS,
} from "./colorStrategy";
import { formatChangePercent } from "./utils";

/**
 * 通用 Heatmap UI 组件。
 *
 * 职责：
 * - 根据传入的 HeatmapNode 列表和容器尺寸渲染 treemap 色块
 * - 管理 hover / 选中状态，以及调用颜色策略计算背景/文字颜色
 * - 暴露 renderCellContent 与 renderTooltip 供业务层自定义展示
 *
 * 注意：
 * - 不负责数据获取与 ThemeItem 映射，只依赖已经抽象好的 HeatmapNode[]
 * - 使用 window.d3 和 ResizeObserver，因此为纯客户端组件
 */

const MIN_FONT_HEIGHT_LARGE = 48;

export const Heatmap: React.FC<HeatmapProps> = (props) => {
  const {
    items,
    config,
    selectedId,
    onSelect,
    onHoverChange,
    renderTooltip,
    className,
    renderCellContent,
  } = props;

  // 从 config 中读取配置，提供默认值
  const showTooltip = config?.showTooltip ?? true;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [layoutNodes, setLayoutNodes] = useState<HeatmapLayoutNode[]>([]);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{
    node: HeatmapLayoutNode;
    x: number;
    y: number;
    adjusted: boolean;
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      const w = el.clientWidth || Math.round(rect.width);
      const h = el.clientHeight || Math.round(rect.height);
      if (w > 0 && h > 0) {
        setSize({ width: w, height: h });
      }
    };

    updateSize();

    const r1 = requestAnimationFrame(updateSize);
    const t1 = setTimeout(updateSize, 50);
    const t2 = setTimeout(updateSize, 150);
    const t3 = setTimeout(updateSize, 350);
    const t4 = setTimeout(updateSize, 600);

    const observer = new ResizeObserver(() => {
      updateSize();
    });
    observer.observe(el);

    return () => {
      cancelAnimationFrame(r1);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      observer.disconnect();
    };
  }, [items]);

  useEffect(() => {
    if (!size.width || !size.height || !items.length) {
      setLayoutNodes([]);
      return;
    }
    const nodes = computeHeatmapLayout({
      items,
      width: size.width,
      height: size.height,
      config,
    });
    setLayoutNodes(nodes);
  }, [items, size.width, size.height, config]);

  const allItemsMemo = useMemo(() => items, [items]);
  const compressSmallCells = config?.compressSmallCells ?? false;
  const minHeightForTwoLines = config?.minHeightForTwoLines ?? 30;

  const tooltipContent = (node: HeatmapLayoutNode) => {
    if (renderTooltip) {
      return renderTooltip(node);
    }
    return (
      <div className="body_s md:text-content_primary text-content_inverse_primary">
        {node.name}
      </div>
    );
  };

  /**
   * 根据色块自身的位置来定位 tooltip，而不是跟随鼠标。
   * 简单地锚定在色块水平中心、顶部稍微偏上，避免重复计算。
   */
  const showTooltipForNode = (node: HeatmapLayoutNode) => {
    setTooltip({
      node,
      x: node.x + node.width / 2,
      y: node.y,
      adjusted: false,
    });
  };

  const clearTooltip = () => {
    setTooltip(null);
  };

  // 根据实际 tooltip 尺寸和容器尺寸调整位置，避免被裁剪
  /**
   * 在 tooltip 首次出现时，根据实际宽高做一次边界校正，避免被裁剪。
   * 使用 adjusted 标记确保每次 hover 只修正一次，避免循环 setState。
   */
  useEffect(() => {
    if (!tooltip || tooltip.adjusted) {
      return;
    }
    const containerEl = containerRef.current;
    const tipEl = tooltipRef.current;
    if (!containerEl || !tipEl) {
      return;
    }

    const containerRect = containerEl.getBoundingClientRect();
    const tipRect = tipEl.getBoundingClientRect();

    const node = tooltip.node;
    const PADDING = 4;

    // 1. 先根据色块水平中心点计算 x，使 tooltip 水平居中于块
    let x = node.x + node.width / 2 - tipRect.width / 2;
    if (x < PADDING) {
      x = PADDING;
    }
    if (x + tipRect.width > containerRect.width - PADDING) {
      x = containerRect.width - tipRect.width - PADDING;
    }

    // 2. 优先放在色块上方，完全不遮挡色块内容
    const yTop = node.y - tipRect.height - 8;
    let y: number;
    if (yTop >= PADDING) {
      y = yTop;
    } else {
      // 上方空间不够时，放到块下方，并留出一定间隔
      let yBottom = node.y + node.height + 8;
      if (yBottom + tipRect.height > containerRect.height - PADDING) {
        yBottom = containerRect.height - tipRect.height - PADDING;
      }
      y = Math.max(PADDING, yBottom);
    }

    setTooltip((prev) =>
      prev
        ? {
            ...prev,
            x,
            y,
            adjusted: true,
          }
        : prev,
    );
  }, [tooltip]);

  return (
    <div
      ref={containerRef}
      className={cn(
        // 外部可以通过 className 进一步控制宽高 / 间距
        "relative w-full h-full overflow-hidden bg-background_white_overlay5",
        className,
      )}
      style={{
        borderRadius: config?.borderRadius ?? DEFAULT_BORDER_RADIUS,
        ...props.style,
      }}
    >
      {layoutNodes.map((node) => {
        const isSelected = selectedId != null && selectedId === node.id;
        const isHovered = hoverId === node.id;
        const fill = getFillColor({ node, allItems: allItemsMemo, config });
        const textColorClass = getTextColor({
          isSelected,
          isHovered,
          config,
        });

        // 判断是否是四个角的元素（一个元素可能占据多个角）
        const TOLERANCE = 1; // 允许1px的误差
        const corners: Array<
          "top-left" | "top-right" | "bottom-left" | "bottom-right"
        > = [];

        // 检查 top-left
        if (Math.abs(node.x) < TOLERANCE && Math.abs(node.y) < TOLERANCE) {
          corners.push("top-left");
        }
        // 检查 top-right
        if (
          Math.abs(node.x + node.width - size.width) < TOLERANCE &&
          Math.abs(node.y) < TOLERANCE
        ) {
          corners.push("top-right");
        }
        // 检查 bottom-left
        if (
          Math.abs(node.x) < TOLERANCE &&
          Math.abs(node.y + node.height - size.height) < TOLERANCE
        ) {
          corners.push("bottom-left");
        }
        // 检查 bottom-right
        if (
          Math.abs(node.x + node.width - size.width) < TOLERANCE &&
          Math.abs(node.y + node.height - size.height) < TOLERANCE
        ) {
          corners.push("bottom-right");
        }

        const corner = corners.length > 0 ? corners : null;

        const borderStyle = getBorderStyle({
          isSelected,
          isHovered,
          config,
          corner,
          borderRadius: config?.borderRadius,
        });

        // 根据「可用面积 + 最小边」动态调整字号，避免高但极窄的格子里只露出一两个字母
        const area = node.width * node.height;
        const minSide = Math.min(node.width, node.height);

        // 标题字体 3 档：body_medium_l / m / s
        let titleClass: string;
        if (area >= 12000 && minSide >= 80) {
          // titleClass = "body_medium_l";
          titleClass = "body_medium_m";
        } else if (area >= 4500 && minSide >= MIN_FONT_HEIGHT_LARGE) {
          // titleClass = "body_medium_m";
          titleClass = "body_medium_s";
        } else {
          titleClass = "body_medium_s";
        }

        // 涨跌幅字体 2 档：body_s / body_xs
        let changeClass: string;
        if (area >= 4500 && minSide >= 32) {
          changeClass = "body_s";
        } else {
          changeClass = "body_xs";
        }

        // 小尺寸格子的文案展示控制：
        // - 当 compressSmallCells=true 时：
        //   - 非常矮的格子：只显示底色，不渲染任何文字；
        //   - 中等高度：只显示第一行标题，不显示涨跌幅；
        //   - 高度充足：正常显示两行。
        let showTitle = true;
        let showChange = node.changePercent24h != null;
        if (compressSmallCells) {
          const h = node.height;
          if (h < 14) {
            showTitle = false;
            showChange = false;
          } else if (h < minHeightForTwoLines) {
            showTitle = true;
            showChange = false;
          }
        }

        const renderContext: HeatmapRenderContext = {
          isSelected,
          isHovered,
          titleClass,
          changeClass,
        };

        return (
          <div
            key={node.id}
            className={cn(
              "absolute cursor-pointer text-center transition-[transform,box-shadow] duration-150 ease-out",
            )}
            style={{
              left: node.x,
              top: node.y,
              width: node.width,
              height: node.height,
              backgroundColor: fill,
              ...borderStyle,
            }}
            aria-label="Heatmap Cell"
            data-openreplay-label="misc_heatmapcellsemantic_click"
            id="heatmap-cell"
            onMouseEnter={() => {
              setHoverId(node.id);
              // 根据配置决定是否显示 tooltip
              if (showTooltip) {
                showTooltipForNode(node);
              }
              onHoverChange?.(node);
            }}
            onMouseLeave={() => {
              setHoverId((prev) => (prev === node.id ? null : prev));
              clearTooltip();
              onHoverChange?.(null);
            }}
            onClick={() => {
              onSelect?.(node);
            }}
          >
            {renderCellContent ? (
              renderCellContent(node, renderContext)
            ) : (
              <div className="flex h-full w-full flex-col items-start justify-center p-1">
                {showTitle && (
                  <div
                    className={cn(
                      titleClass,
                      textColorClass,
                      "w-full overflow-hidden text-ellipsis whitespace-nowrap",
                    )}
                  >
                    {node.name}
                  </div>
                )}
                {showChange && node.changePercent24h != null && (
                  <div
                    className={cn(
                      changeClass,
                      textColorClass,
                      "mt-0.5 w-full overflow-hidden text-ellipsis whitespace-nowrap",
                    )}
                  >
                    {formatChangePercent(node.changePercent24h, {
                      zeroAsDash: false,
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* 根据配置决定是否显示 tooltip */}
      {tooltip && showTooltip && (
        <div
          ref={tooltipRef}
          className="pointer-events-none absolute z-20"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltipContent(tooltip.node)}
        </div>
      )}
    </div>
  );
};
